import { type AutomergeUrl } from "@automerge/automerge-repo";
import type { MarkdownCommentSelection } from "forest/markdown-editor/EditorToolbar";
import { MarkdownEditor } from "forest/markdown-editor/MarkdownEditor";
import { carryRange } from "playground/annotations/annotations";
import { isDirInode } from "playground/fs/paths";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { toast } from "sonner";

import { useTextComments } from "@/comments/text-comments";
import { useBearSandbox } from "@/components/BearSandboxProvider";
import { useWorkspacePane } from "@/panes/Workspace";
import { makeSketchCodePreview } from "@/sketch/sketch-embed";

import "@/components/markdown-editor.css";
// KaTeX for the editor's math nodes.
import "katex/dist/katex.min.css";
import "prosemirror-view/style/prosemirror.css";

type DocPaneProps = {
  paneId: string;
  filename: string;
  url?: AutomergeUrl;
  /** Open another project file (the ```sketch embed's open button). */
  onOpenFile?: (filename: string) => void;
  /** A heading to scroll to (the agent's `show` tool). */
  revealHeading?: { text: string };
  /** A comment thread to scroll to and open (the `show` tool, a chat link). */
  revealThread?: { threadId: string };
};

const DocPane = ({
  paneId,
  filename,
  url,
  onOpenFile,
  revealHeading,
  revealThread,
}: DocPaneProps) => {
  const { focusPane, isFocused } = useWorkspacePane(paneId);
  const { playground } = useBearSandbox();
  const comments = useTextComments(url, filename);
  const [revealComment, setRevealComment] = useState<{
    source: string;
    range: { start: number; end: number };
  }>();

  // The toolbar measures its selection in the editor's own serialization of
  // the file; threads anchor into the stored text.
  const startDraft = (selection: MarkdownCommentSelection) => {
    const stored = comments.source;
    const range =
      stored === undefined
        ? null
        : carryRange(selection.source, stored, selection);
    if (!stored || !range) {
      toast.error("Could not map this selection to the file's text.");
      return;
    }
    comments.startDraft({
      ...range,
      quote: stored.slice(range.start, range.end),
      display: selection.display,
    });
  };

  const reveal = (threadId: string) => {
    const thread = comments.threads.find((t) => t.annotation.id === threadId);
    comments.activate(threadId);
    if (thread?.range && comments.source !== undefined) {
      setRevealComment({ source: comments.source, range: thread.range });
    }
  };
  const revealRef = useRef(reveal);
  revealRef.current = reveal;
  const loaded = comments.source !== undefined;
  useEffect(() => {
    if (revealThread && loaded) revealRef.current(revealThread.threadId);
  }, [revealThread, loaded]);

  // The markdown editor reads the preview map once at construction, so the
  // live callbacks come off refs (the floorplan preview's own pattern).
  const openFileRef = useRef(onOpenFile);
  useEffect(() => {
    openFileRef.current = onOpenFile;
  }, [onOpenFile]);
  const playgroundRef = useRef(playground);
  useEffect(() => {
    playgroundRef.current = playground;
  }, [playground]);

  const codeBlockPreviews = useMemo(
    () => ({
      sketch: makeSketchCodePreview({
        async resolvePath(path) {
          const inode = await playgroundRef.current.stat(path);
          if (!inode || isDirInode(inode)) return null;
          // Only actual sketches: mounting a `{ text }` doc as a
          // TLStoreSnapshot would take the whole preview down.
          if (inode.mimeType !== "automerge/tldraw") return null;
          return inode.uri.startsWith("automerge:")
            ? (inode.uri as AutomergeUrl)
            : null;
        },
        openFile(path) {
          openFileRef.current?.(path);
        },
      }),
    }),
    [],
  );

  const handlePointerDownCapture = useCallback(() => {
    focusPane();
  }, [focusPane]);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.stopPropagation();
    },
    [],
  );

  return (
    <div
      className="relative flex h-full"
      data-workspace-pane
      onPointerDownCapture={handlePointerDownCapture}
      onPointerDown={handlePointerDown}
    >
      <MarkdownEditor
        playground={playground}
        path={filename}
        placeholder="Start writing specs…"
        // Follow the workspace's focused pane, so typing lands where the user
        // is looking without a second click into the document.
        isFocused={isFocused}
        revealHeading={revealHeading}
        codeBlockPreviews={codeBlockPreviews}
        showToc
        className="bear-doc-editor flex h-full min-w-0 flex-1 flex-col bg-card"
        onCommentSelection={url ? startDraft : undefined}
        revealComment={revealComment}
        comments={
          comments.source === undefined
            ? undefined
            : {
                source: comments.source,
                threads: comments.highlights,
                activeId: comments.activeId,
                onActivate: comments.activate,
                card: comments.card,
              }
        }
      />
    </div>
  );
};

export default DocPane;
